/**
 * Notes query hooks using TanStack Query
 */

import { noteApi } from '@/lib/api/notes.api'
import { useOrganizationStore } from '@/store/organization.store'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  Note,
  NoteVisibilityEnum,
  NoteWithRelations,
  UpdateNoteInput,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'

export interface NoteFilters {
  search?: string
  visibility?: NoteVisibilityEnum
  creatorId?: string
  isPinned?: boolean
  isCollection?: boolean
  /** 'personal' = notes created by or shared with the current user */
  scope?: 'personal'
  /** true = only archived notes; omitted/false = only non-archived */
  archived?: boolean
  moduleType?: string
  moduleId?: string
  collaborativeOnly?: boolean
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface PaginatedResponse<T> {
  data: T[]
  meta: {
    totalItems: number
    totalPages: number
    currentPage: number
    pageSize: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

/**
 * Hook for fetching notes list with filters
 */
export function useNotesQuery(
  filters?: NoteFilters,
  options?: { enabled?: boolean; staleTime?: number },
) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: queryKeys.notes.list({ ...filters, orgId }),
    queryFn: async (): Promise<PaginatedResponse<NoteWithRelations>> => {
      const response = await noteApi.getNotes({
        search: filters?.search,
        visibility: filters?.visibility,
        creatorId: filters?.creatorId,
        isPinned: filters?.isPinned,
        isCollection: filters?.isCollection,
        scope: filters?.scope,
        archived: filters?.archived,
        moduleType: filters?.moduleType,
        moduleId: filters?.moduleId,
        page: filters?.page || 1,
        limit: filters?.limit || 50,
      })

      let notes = response.notes

      // Filter collaborative only if specified (all non-private notes: TEAM, SHARED, RECORD)
      if (filters?.collaborativeOnly) {
        notes = notes.filter(note => note.visibility !== 'PRIVATE')
      }

      // Apply client-side sorting if specified
      if (filters?.sortBy) {
        notes = [...notes].sort((a, b) => {
          const aVal = a[filters.sortBy as keyof NoteWithRelations]
          const bVal = b[filters.sortBy as keyof NoteWithRelations]

          if (aVal === null || aVal === undefined) return 1
          if (bVal === null || bVal === undefined) return -1

          let comparison = 0
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            comparison = aVal.localeCompare(bVal)
          } else if (aVal instanceof Date && bVal instanceof Date) {
            comparison = aVal.getTime() - bVal.getTime()
          } else if (typeof aVal === 'number' && typeof bVal === 'number') {
            comparison = aVal - bVal
          } else {
            comparison = String(aVal).localeCompare(String(bVal))
          }

          return filters.sortOrder === 'desc' ? -comparison : comparison
        })
      }

      return {
        data: notes,
        meta: {
          totalItems: filters?.collaborativeOnly
            ? notes.length
            : response.total,
          totalPages: Math.ceil(
            (filters?.collaborativeOnly ? notes.length : response.total) /
              (filters?.limit || 50),
          ),
          currentPage: filters?.page || 1,
          pageSize: filters?.limit || 50,
          hasNextPage:
            (filters?.page || 1) <
            Math.ceil(
              (filters?.collaborativeOnly ? notes.length : response.total) /
                (filters?.limit || 50),
            ),
          hasPreviousPage: (filters?.page || 1) > 1,
        },
      }
    },
    enabled: !!orgId && options?.enabled !== false,
    placeholderData: previous => previous,
    staleTime: options?.staleTime ?? 30000,
  })
}

/**
 * Hook for fetching a single note by ID
 */
export function useNoteQuery(
  noteId: string | undefined,
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: queryKeys.notes.detail(noteId || ''),
    queryFn: () => noteApi.getNote(noteId!),
    enabled: !!orgId && !!noteId && options?.enabled !== false,
    staleTime: 30000,
  })
}

/**
 * Hook for fetching note categories
 */
export function useNoteCategoriesQuery(
  params?: { moduleType?: string; moduleId?: string },
  options?: { enabled?: boolean },
) {
  const { currentOrganization } = useOrganizationStore()
  const orgId = currentOrganization?.id

  return useQuery({
    queryKey: queryKeys.notes.categories({ ...params, orgId }),
    queryFn: () => noteApi.getCategories(params),
    enabled: !!orgId && options?.enabled !== false,
    staleTime: 60000,
  })
}

/**
 * Hook for updating a note field with optimistic updates
 */
export function useUpdateNoteFieldMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      field,
      value,
    }: {
      id: string
      field: string
      value: unknown
    }) => {
      const updateData: UpdateNoteInput = { [field]: value }
      return noteApi.updateNote(id, updateData)
    },
    onMutate: async ({ id, field, value }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.notes.lists() })
      await queryClient.cancelQueries({ queryKey: queryKeys.notes.detail(id) })

      // Snapshot the previous values for rollback
      const previousListQueries = queryClient.getQueriesData<
        PaginatedResponse<NoteWithRelations>
      >({
        queryKey: queryKeys.notes.lists(),
      })
      const previousDetail = queryClient.getQueryData<NoteWithRelations>(
        queryKeys.notes.detail(id),
      )

      // Optimistically update all list queries
      queryClient.setQueriesData<PaginatedResponse<NoteWithRelations>>(
        { queryKey: queryKeys.notes.lists() },
        old => {
          if (!old) return old
          const isCustomField = field.startsWith('custom_')
          const customFieldName = isCustomField
            ? field.replace('custom_', '')
            : null
          return {
            ...old,
            data: old.data.map(note => {
              if (note.id !== id) return note
              if (!isCustomField || !customFieldName) {
                return { ...note, [field]: value }
              }
              const noteRecord = note as Note & {
                customFields?: Record<string, unknown>
              }
              return {
                ...note,
                [field]: value,
                customFields: {
                  ...(noteRecord.customFields || {}),
                  [customFieldName]: value,
                },
              }
            }),
          }
        },
      )

      // Optimistically update the detail query
      if (previousDetail) {
        const isCustomField = field.startsWith('custom_')
        const customFieldName = isCustomField
          ? field.replace('custom_', '')
          : null
        if (!isCustomField || !customFieldName) {
          queryClient.setQueryData<NoteWithRelations>(queryKeys.notes.detail(id), {
            ...previousDetail,
            [field]: value,
          })
        } else {
          const detailRecord = previousDetail as Note & {
            customFields?: Record<string, unknown>
          }
          queryClient.setQueryData<NoteWithRelations>(queryKeys.notes.detail(id), {
            ...previousDetail,
            [field]: value,
            customFields: {
              ...(detailRecord.customFields || {}),
              [customFieldName]: value,
            },
          } as NoteWithRelations)
        }
      }

      return { previousListQueries, previousDetail }
    },
    onError: (_err, { id }, context) => {
      // Rollback to the previous values on error
      if (context?.previousListQueries) {
        for (const [queryKey, data] of context.previousListQueries) {
          queryClient.setQueryData(queryKey, data)
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(
          queryKeys.notes.detail(id),
          context.previousDetail,
        )
      }
    },
    onSettled: (_data, _error, { id }) => {
      // Refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.detail(id) })
    },
  })
}

/**
 * Hook for updating a note
 */
export function useUpdateNoteMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateNoteInput }) => {
      return noteApi.updateNote(id, data)
    },
    onSuccess: updatedNote => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.lists() })
      queryClient.setQueryData(
        queryKeys.notes.detail(updatedNote.id),
        updatedNote,
      )
    },
  })
}

/**
 * Hook for creating a note
 */
export function useCreateNoteMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: noteApi.createNote.bind(noteApi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.lists() })
    },
  })
}

/**
 * Hook for deleting a note
 */
export function useDeleteNoteMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (noteId: string) => noteApi.deleteNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.lists() })
    },
  })
}

/**
 * Hook for bulk deleting notes
 */
export function useBulkDeleteNotesMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => noteApi.bulkDeleteNotes(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.lists() })
    },
  })
}

/**
 * useNotesQuery under a non-hook name for useModuleTable configs: passing a
 * use*-named import as a value makes React Compiler bail out of the consuming
 * component. Same function, only ever called unconditionally inside the hook.
 */
export const notesListQuery = useNotesQuery
